import { type TApiResponse } from "../../types/api";
import { ApiService, type TApiServiceError, type TApiServiceOptions } from "../apiService";

import { handleAsync } from "@/utils";
import { withAuthHooks } from "../auth/auth.ky.hooks";

import {
  type TDashboardCategory,
  type TDashboardTool,
  type TSectionInitialData,
  type TSectionInitialDataResponse,
} from "./dashboard.types";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class DashboardService {
  private rootEndpoint = "dashboard" as const;

  getSectionInitialData(sectionId: string, companyId?: string | null, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TSectionInitialData>>(async () => {
      const response = await apiService.get<TApiResponse<TSectionInitialDataResponse>>(
        `${this.rootEndpoint}/section/${sectionId}/initialdata`,
        {
          ...(companyId ? { searchParams: { company_id: companyId } } : {}),
          ...options,
        }
      );

      const categories: TDashboardCategory[] = Array.isArray(response.data?.categories)
        ? response.data.categories
            .map((category) => ({
              categoryId: category["category-id"],
              categoryName: category["category-name"],
              categoryOrder: category["category-order"],
            }))
            .sort((a, b) => a.categoryOrder - b.categoryOrder)
        : [];

      const tools: TDashboardTool[] = Array.isArray(response.data?.tools)
        ? response.data.tools.map((tool) => ({
            name: tool.name,
            description: tool.description,
            url: tool.url,
            categoryIds: Array.isArray(tool["category-ids"]) ? tool["category-ids"] : [],
          }))
        : [];

      return {
        ...response,
        data: {
          section: {
            sectionId: response.data.section["section-id"],
            sectionName: response.data.section["section-name"],
          },
          categories,
          tools,
        },
      };
    });
  }
}

export const dashboardService = new DashboardService();
